import React, { useState } from 'react';
import { Lock, Eye, EyeOff, Mail, User, ArrowRight, Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

interface PasswordGateProps {
  onAuthenticated: () => void;
}

export function PasswordGate({ onAuthenticated }: PasswordGateProps) {
  const { login, register, isAuthenticated } = useAuth();
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // If already authenticated, trigger callback
  React.useEffect(() => {
    if (isAuthenticated) {
      onAuthenticated();
    }
  }, [isAuthenticated, onAuthenticated]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    // Superadmin access - product owner/developer
    const superadminEmail = 'admin@vhss.org';
    const superadminPassword = 'VenusLTSS2024!';
    
    if (email.toLowerCase().trim() === superadminEmail && password === superadminPassword) {
      localStorage.setItem('ltss_subscription', JSON.stringify({ 
        plan: 'enterprise', 
        status: 'active',
        stripe_customer_id: 'superadmin'
      }));
      localStorage.setItem('ltss_user', JSON.stringify({ 
        email: superadminEmail, 
        name: 'Superadmin', 
        id: 'superadmin',
        role: 'superadmin'
      }));
      localStorage.setItem('ltss_superadmin', 'true');
      setIsLoading(false);
      window.location.reload();
      return;
    }
    
    // Wrong superadmin password
    if (email.toLowerCase().trim() === superadminEmail && password !== superadminPassword) {
      setError('Invalid credentials');
      setIsLoading(false);
      return;
    }

    // Test Enterprise account - for testing Organization Panel
    const testEnterpriseEmail = 'enterprise@test.com';
    const testEnterprisePassword = 'Test1234!';
    
    if (email.toLowerCase().trim() === testEnterpriseEmail) {
      if (password === testEnterprisePassword) {
        localStorage.setItem('ltss_subscription', JSON.stringify({ 
          plan: 'enterprise', 
          status: 'active',
          stripe_customer_id: 'test_enterprise'
        }));
        localStorage.setItem('ltss_user', JSON.stringify({ 
          email: testEnterpriseEmail, 
          name: 'Enterprise Test User', 
          id: 'test_enterprise',
          role: 'owner'
        }));
        setIsLoading(false);
        window.location.reload();
        return;
      } else {
        setError('Invalid credentials');
        setIsLoading(false);
        return;
      }
    }

    // Test Pro account - for testing Pro features
    const testProEmail = 'pro@test.com';
    const testProPassword = 'Test1234!';
    
    if (email.toLowerCase().trim() === testProEmail) {
      if (password === testProPassword) {
        localStorage.setItem('ltss_subscription', JSON.stringify({ 
          plan: 'pro', 
          status: 'active',
          stripe_customer_id: 'test_pro'
        }));
        localStorage.setItem('ltss_user', JSON.stringify({ 
          email: testProEmail, 
          name: 'Pro Test User', 
          id: 'test_pro',
          role: 'owner'
        }));
        setIsLoading(false);
        window.location.reload();
        return;
      } else {
        setError('Invalid credentials');
        setIsLoading(false);
        return;
      }
    }

    try {
      if (mode === 'login') {
        await login(email, password);
      } else {
        if (!name.trim()) {
          setError('Please enter your name');
          setIsLoading(false);
          return;
        }
        await register(email, password, name);
      }
      onAuthenticated();
    } catch (err: any) {
      console.error('Auth error:', err);
      // PocketBase error format
      if (err?.data?.data) {
        const data = err.data.data;
        if (data.email?.message) {
          setError(data.email.message);
        } else if (data.password?.message) {
          setError(data.password.message);
        } else if (data.passwordConfirm?.message) {
          setError(data.passwordConfirm.message);
        } else {
          setError(err.message || 'Validation error');
        }
      } else if (err?.message) {
        setError(err.message);
      } else {
        setError(mode === 'login' ? 'Invalid email or password' : 'Registration failed. Try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-3 sm:p-4">
      <div className="w-full max-w-md">
        <div className="card p-5 sm:p-8">
          {/* Logo/Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-blue-500/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-blue-400" />
            </div>
            <h1 className="text-2xl font-bold text-white">LTSS Analytics</h1>
            <p className="text-slate-400 mt-2">
              {mode === 'login' ? 'Sign in to your account' : 'Create your account'}
            </p>
          </div>

          {/* Auth Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'register' && (
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Full name"
                  className="w-full pl-11 pr-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white 
                           placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 
                           focus:border-transparent transition-all duration-200"
                />
              </div>
            )}

            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email address"
                className="w-full pl-11 pr-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white 
                         placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 
                         focus:border-transparent transition-all duration-200"
                autoFocus
              />
            </div>

            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                className={`w-full pl-11 pr-12 py-3 bg-slate-800 border rounded-lg text-white 
                          placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 
                          focus:border-transparent transition-all duration-200
                          ${error ? 'border-red-500 animate-shake' : 'border-slate-700'}`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-300 transition-colors"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>

            {error && (
              <p className="text-red-400 text-sm text-center">{error}</p>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-600/50 text-white 
                       font-medium rounded-lg transition-colors duration-200 focus:outline-none 
                       focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-slate-900
                       flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <React.Fragment>
                  {mode === 'login' ? 'Sign In' : 'Create Account'}
                  <ArrowRight className="w-4 h-4" />
                </React.Fragment>
              )}
            </button>
          </form>

          {/* Toggle Mode */}
          <div className="mt-6 text-center">
            <button
              onClick={() => {
                setMode(mode === 'login' ? 'register' : 'login');
                setError('');
              }}
              className="text-blue-400 hover:text-blue-300 text-sm transition-colors"
            >
              {mode === 'login' 
                ? "Don't have an account? Sign up" 
                : 'Already have an account? Sign in'}
            </button>
          </div>

          {/* Footer */}
          <div className="text-center mt-6 space-y-2">
            <p className="text-slate-500 text-sm">
              Protected healthcare analytics dashboard
            </p>
            <p className="text-slate-600 text-xs">
              🔒 HIPAA Compliant: All data processing occurs locally in your browser.
              <br />No patient data is transmitted to or stored on our servers.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
